import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Truck, Clock, User, Plus, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Equipamentos from "@/pages/cadastro/Equipamentos";
import { getObraStats } from "@/services/obraStats";

// Equipamento real: linha do equipment do RDO mais recente da obra
interface Equipment {
  id: string;
  name: string;
  type: string;
  condition?: string;
  operator?: string;
}

interface EquipmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Obra cujo último RDO alimenta a lista */
  obraId?: string;
}

export function EquipmentModal({ open, onOpenChange, obraId }: EquipmentModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showCadastroEquipamentos, setShowCadastroEquipamentos] = useState(false);

  const handleAddClick = () => setShowCadastroEquipamentos(true);
  const handleBack = () => setShowCadastroEquipamentos(false);

  // Equipamentos reais do último RDO da obra
  const [equipmentList, setEquipmentList] = useState<Equipment[]>([]);
  const [lastDate, setLastDate] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !obraId) return;
    let active = true;
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        const last = s.rdos[0];
        setLastDate(last?.date ?? null);
        setEquipmentList(
          (last?.equipment ?? []).map((e, i) => ({
            id: String(i),
            name: e.identification || e.type || "Equipamento",
            type: e.type || "—",
            condition: e.condition,
            operator: e.operator,
          }))
        );
      })
      .catch((e) => console.error("EquipmentModal:", e));
    return () => { active = false; };
  }, [open, obraId]);

  const filteredEquipment = equipmentList.filter(equipment =>
    equipment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    equipment.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (equipment.operator ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] flex flex-col">
        {!showCadastroEquipamentos ? (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  <DialogTitle>
                    Equipamentos da Obra
                    {lastDate && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        (último RDO: {lastDate.split("-").reverse().join("/")})
                      </span>
                    )}
                  </DialogTitle>
                </div>
                <Button 
                  onClick={handleAddClick}
                  size="sm"
                  className="flex items-center gap-2"
                  style={{ transform: 'translateX(-5mm)' }}
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </Button>
              </div>
            </DialogHeader>

            <div className="flex-1 flex flex-col gap-4 overflow-hidden">
              {/* Search */}
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Buscar equipamento ou tipo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Equipment Grid */}
              <div className="flex-1 overflow-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filteredEquipment.map((equipment) => (
                    <Card key={equipment.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-sm font-medium line-clamp-2">
                            {equipment.name}
                          </CardTitle>
                          {equipment.condition && (
                            <Badge variant="secondary" className="ml-2 flex-shrink-0">
                              {equipment.condition}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{equipment.type}</span>
                          </div>
                          {equipment.operator && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <User className="w-3 h-3" />
                              <span>Operador: {equipment.operator}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {filteredEquipment.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum equipamento encontrado</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBack}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <DialogTitle>Cadastro de Equipamentos</DialogTitle>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              <Equipamentos />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}